import { Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import {
  Target, AlertTriangle, FileText, Sparkles,
  TrendingUp, TrendingDown, Minus, Link2,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { toast } from 'sonner'
import {
  governanceService, RiskRegister, DecisionLog, Narrative,
} from '../services/governance'
import { getErrorMessage } from '../utils/apiTransformers'

type Tab = 'narrative' | 'objectives' | 'risks' | 'decisions'

const SEVERITY_CLASS: Record<string, string> = {
  critical: 'bg-red-100 text-red-800',
  high: 'bg-orange-100 text-orange-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-green-100 text-green-800',
  unscored: 'bg-gray-100 text-gray-600',
}

const SENTIMENT_ICON = {
  up: <TrendingUp className="w-4 h-4" />,
  down: <TrendingDown className="w-4 h-4" />,
  flat: <Minus className="w-4 h-4" />,
}

export default function GovernanceSurfaces() {
  const [tab, setTab] = useState<Tab>('narrative')

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Governance Surfaces</h1>
        <p className="text-gray-500">
          Risk register, decision log, strategic objectives, and an auto-narrative —
          all built on real numbers across your portfolio.
        </p>
      </div>

      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {([
          ['narrative', 'Narrative', Sparkles],
          ['objectives', 'Objectives (OKR)', Target],
          ['risks', 'Risk Register', AlertTriangle],
          ['decisions', 'Decision Log', FileText],
        ] as [Tab, string, LucideIcon][]).map(([id, label, Icon]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === 'narrative' && <NarrativePanel />}
      {tab === 'objectives' && (
        <div className="rounded-lg border border-border bg-card p-6">
          <p className="text-sm text-foreground">
            Objectives and key results now live on the Strategy page, with progress against time and the
            portfolios, programs and projects that deliver each one.
          </p>
          <Link to="/strategy" className="mt-3 inline-block text-sm font-medium text-primary underline">Open Strategy</Link>
        </div>
      )}
      {tab === 'risks' && <RiskRegisterPanel />}
      {tab === 'decisions' && <DecisionLogPanel />}
    </div>
  )
}

// ── A9.4 Narrative ────────────────────────────────────────────────────────────
function NarrativePanel() {
  const [data, setData] = useState<Narrative | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    governanceService.getNarrative({ period_days: 30 })
      .then(setData)
      .catch(e => toast.error(getErrorMessage(e)))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <p className="text-gray-500">Loading…</p>
  if (!data) return <p className="text-gray-500">No narrative available.</p>

  return (
    <div>
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 text-blue-800 font-semibold mb-1">
          <Sparkles className="w-4 h-4" /> This period vs. last {data.period_days} days
        </div>
        <p className="text-gray-800">{data.headline}</p>
      </div>
      <div className="space-y-2">
        {data.points.map((p, i) => (
          <div key={i} className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg">
            <span className={`mt-0.5 ${
              p.sentiment === 'positive' ? 'text-green-600'
              : p.sentiment === 'negative' ? 'text-red-600' : 'text-gray-400'
            }`}>
              {SENTIMENT_ICON[p.direction]}
            </span>
            <p className="text-sm text-gray-700">{p.text}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── A9.1 Risk Register ──────────────────────────────────────────────────────
function RiskRegisterPanel() {
  const [data, setData] = useState<RiskRegister | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    governanceService.getRiskRegister()
      .then(setData)
      .catch(e => toast.error(getErrorMessage(e)))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <p className="text-gray-500">Loading…</p>
  if (!data) return <p className="text-gray-500">No risks.</p>

  return (
    <div>
      <div className="flex gap-3 mb-4">
        {(['critical', 'high', 'medium', 'low', 'unscored'] as const).map(s => (
          <div key={s} className={`px-3 py-2 rounded-lg text-sm font-medium ${SEVERITY_CLASS[s]}`}>
            {data.by_severity[s] || 0} {s}
          </div>
        ))}
      </div>

      {data.entries.length === 0 ? (
        <p className="text-gray-500">No risks across projects or programs.</p>
      ) : (
        <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">Risk</th>
                <th className="px-3 py-2 font-medium">Source</th>
                <th className="px-3 py-2 font-medium">L×I</th>
                <th className="px-3 py-2 font-medium">Score</th>
                <th className="px-3 py-2 font-medium">Severity</th>
                <th className="px-3 py-2 font-medium">Owner</th>
                <th className="px-3 py-2 font-medium">Links</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map(e => (
                <tr key={`${e.source}-${e.raid_id}`} className="border-t border-gray-100">
                  <td className="px-3 py-2 text-gray-900">{e.title}</td>
                  <td className="px-3 py-2 text-gray-500">{e.source_name} <span className="text-gray-400">({e.source})</span></td>
                  <td className="px-3 py-2 tabular-nums">{e.likelihood}×{e.impact}</td>
                  <td className="px-3 py-2 tabular-nums">{e.score}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${SEVERITY_CLASS[e.severity]}`}>{e.severity}</span>
                  </td>
                  <td className="px-3 py-2 text-gray-500">{e.owner || '—'}</td>
                  <td className="px-3 py-2 text-gray-500">
                    <span className="flex items-center gap-1"><Link2 className="w-3 h-3" />{e.link_count}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── A9.2 Decision Log ────────────────────────────────────────────────────────
function DecisionLogPanel() {
  const [data, setData] = useState<DecisionLog | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    governanceService.getDecisionLog()
      .then(setData)
      .catch(e => toast.error(getErrorMessage(e)))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <p className="text-gray-500">Loading…</p>
  if (!data || data.entries.length === 0) return <p className="text-gray-500">No decisions logged.</p>

  return (
    <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500 text-left">
          <tr>
            <th className="px-3 py-2 font-medium">Decision</th>
            <th className="px-3 py-2 font-medium">Source</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Impact</th>
            <th className="px-3 py-2 font-medium">Decided</th>
          </tr>
        </thead>
        <tbody>
          {data.entries.map(e => (
            <tr key={`${e.source}-${e.decision_id}`} className="border-t border-gray-100">
              <td className="px-3 py-2 text-gray-900">{e.title}</td>
              <td className="px-3 py-2 text-gray-500">{e.source_name} <span className="text-gray-400">({e.source})</span></td>
              <td className="px-3 py-2 text-gray-500">{e.status || '—'}</td>
              <td className="px-3 py-2 text-gray-500">{e.impact || '—'}</td>
              <td className="px-3 py-2 text-gray-500">{e.decided_at || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
